import { Box, Flex, Text, chakra } from '@chakra-ui/react'
import { SquareArrowOutUpRight, TriangleAlert } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { LucideProps } from 'lucide-react'
import { Tooltip } from '@/shared/ui/tooltip'
import { NavItemContent } from './NavItem'
import { sidebarStyles } from './styles'

export const REPORT_VIOLATION_URL =
  'http://murojaat.ssv.uz/report#source=DjKn5sSP3L1lBPz30YGO1tcN_i9LTbtTYi83gbxRB_k'

const REPORT_VIOLATION_DOMAIN = 'murojaat.ssv.uz'

/** Filled triangle; the exclamation mark is re-stroked in CSS so it stays visible. */
function FilledTriangleAlert(props: LucideProps) {
  return <TriangleAlert {...props} fill="currentColor" />
}

/** The theme has no amber scale; these are the Tailwind amber values. */
const amber = {
  100: '#FEF3C7',
  400: '#FBBF24',
  500: '#F59E0B',
  '500/15': 'rgba(245, 158, 11, 0.15)',
} as const

const popoverBorder = { base: 'neutral.200', _dark: 'whiteAlpha.100' }

interface ReportViolationLinkProps {
  open: boolean
}

export function ReportViolationLink({ open }: ReportViolationLinkProps) {
  const { t } = useTranslation()

  return (
    <Box px={open ? 3 : 2} pt={2} flexShrink={0}>
      <Tooltip
        showArrow
        openDelay={150}
        closeDelay={100}
        positioning={{ placement: 'right', offset: { mainAxis: 12 } }}
        contentProps={{
          maxW: '72',
          p: 0,
          borderRadius: 'xl',
          borderWidth: '1px',
          borderColor: popoverBorder,
          boxShadow: 'xl',
          textStyle: 'sm',
          css: {
            // The arrow reads --tooltip-bg; its tip border matches the card's.
            '--tooltip-bg': 'colors.white',
            color: 'neutral.900',
            '& [data-part=arrow-tip]': { borderColor: popoverBorder.base },
            _dark: {
              '--tooltip-bg': 'colors.neutral.900',
              color: 'neutral.100',
              '& [data-part=arrow-tip]': { borderColor: popoverBorder._dark },
            },
          },
        }}
        content={
          <>
            <Flex p={4} gap={3} align="flex-start">
              <Flex
                align="center"
                justify="center"
                boxSize={8}
                flexShrink={0}
                borderRadius="lg"
                bg={{ base: amber[100], _dark: amber['500/15'] }}
                color={{ base: amber[500], _dark: amber[400] }}
              >
                <TriangleAlert size={16} />
              </Flex>
              <Text fontSize="sm" fontWeight="medium" lineHeight="snug">
                {t('sidebar.report_violation_hint')}
              </Text>
            </Flex>
            <Flex
              mx={4}
              py={3}
              gap={2}
              align="center"
              borderTopWidth="1px"
              borderColor={popoverBorder}
              fontSize="xs"
              fontWeight="normal"
              color={{ base: 'neutral.500', _dark: 'neutral.400' }}
            >
              <SquareArrowOutUpRight size={14} />
              <Text>{REPORT_VIOLATION_DOMAIN}</Text>
            </Flex>
          </>
        }
      >
        <chakra.a
          href={REPORT_VIOLATION_URL}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={t('sidebar.report_violation')}
          display="block"
          textDecoration="none"
          borderRadius="16px"
          bg={open ? sidebarStyles.hoverBg : 'transparent'}
          outline="none"
          _focusVisible={{ boxShadow: `0 0 0 2px ${sidebarStyles.fgActive}` }}
          css={{
            '& svg path:not(:first-of-type)': {
              stroke: sidebarStyles.markOnFill,
            },
            _dark: {
              '& svg path:not(:first-of-type)': {
                stroke: sidebarStyles.markOnFillDark,
              },
            },
          }}
        >
          <NavItemContent
            open={open}
            icon={FilledTriangleAlert}
            label={t('sidebar.report_violation')}
          />
        </chakra.a>
      </Tooltip>
    </Box>
  )
}
